import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Seo, { articleLd, breadcrumbLd } from "../components/Seo";
import { logger } from "../lib/logger";

const resolveImg = (url) =>
  url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;

export default function ContentPage({ kind = "page" }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/content/${slug}`)
      .then(({ data }) => setPage(data))
      .catch(() => navigate(kind === "post" ? "/journal" : "/"))
      .finally(() => setLoading(false));
  }, [slug, navigate, kind]);

  if (loading) return <div className="p-24 label-eyebrow text-[#555]">Loading…</div>;
  if (!page) return null;

  const isPost = page.type === "post";

  return (
    <article data-testid="content-page" className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
      <Seo
        title={page.title}
        description={
          page.excerpt ||
          (page.body ? `${page.body.replace(/[#*_`>]/g, "").slice(0, 160).trim()}…` : `Read ${page.title} on AfricaArtMasters.`)
        }
        type={isPost ? "article" : "website"}
        image={resolveImg(page.cover_image)}
        imageAlt={page.title}
        path={isPost ? `/journal/${slug}` : `/p/${slug}`}
        jsonLd={
          isPost
            ? [
                articleLd({
                  article: page,
                  url: `${window.location.origin}/journal/${slug}`,
                  image: resolveImg(page.cover_image),
                }),
                breadcrumbLd([
                  { name: "Home", path: "/" },
                  { name: "African Stories", path: "/journal" },
                  { name: page.title, path: `/journal/${slug}` },
                ]),
              ]
            : null
        }
      />
      <Link to={isPost ? "/journal" : "/"} className="label-eyebrow text-[#555] hover:text-[#1E3A8A]">
        ← {isPost ? "All Journal posts" : "Home"}
      </Link>
      {isPost && page.cover_image && (
        <div className="mt-10 mb-12 aspect-[16/9] bg-[#F2EDE5] overflow-hidden">
          <img src={resolveImg(page.cover_image)} alt={`${page.title} — African Stories cover image`} className="w-full h-full object-cover" />
        </div>
      )}
      <header className="mt-10 max-w-3xl">
        <p className="label-eyebrow text-[#1E3A8A]">
          — {isPost ? "Journal" : "From AfricaArtMasters"} · {page.updated_at?.slice(0, 10)}
        </p>
        <h1 data-testid="content-title" className="font-serif-display text-5xl md:text-7xl tracking-tighter leading-[1.02] font-medium mt-4 text-[#111]">
          {page.title}
        </h1>
        {page.excerpt && <p className="mt-6 text-xl text-[#333] leading-relaxed font-light">{page.excerpt}</p>}
      </header>
      <div
        data-testid="content-body"
        className="mt-12 max-w-3xl prose prose-neutral max-w-none [&_h1]:font-serif-display [&_h2]:font-serif-display [&_h3]:font-serif-display [&_h2]:text-3xl [&_h2]:font-medium [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-[#111] [&_h3]:text-xl [&_h3]:font-medium [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:text-lg [&_p]:text-[#333] [&_p]:leading-relaxed [&_p]:font-light [&_p]:my-5 [&_ul]:my-5 [&_li]:my-2 [&_li]:text-[#333] [&_strong]:font-semibold [&_strong]:text-[#111] [&_a]:text-[#1E3A8A] [&_a]:underline [&_em]:text-[#1E3A8A]"
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{page.body}</ReactMarkdown>
      </div>

      {/* v1.24 — Optional embedded video for journalist video reports. */}
      {isPost && page.video_url && (
        <div className="mt-12 max-w-4xl" data-testid="content-video">
          <p className="label-eyebrow text-[#1E3A8A] mb-3">— Video report</p>
          <div className="aspect-video bg-black">
            {isSelfHostedVideo(page.video_url) ? (
              <video
                data-testid="content-video-native"
                src={resolveImg(page.video_url)}
                controls
                preload="metadata"
                className="w-full h-full"
              >
                <track kind="captions" />
              </video>
            ) : (
              <iframe
                src={toEmbedSrc(page.video_url)}
                title={page.title}
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </div>
        </div>
      )}

      {/* v1.24 — Photo gallery for journalist photo essays. */}
      {isPost && Array.isArray(page.gallery) && page.gallery.length > 0 && (
        <div className="mt-16 max-w-5xl" data-testid="content-gallery">
          <p className="label-eyebrow text-[#1E3A8A] mb-4">— Photo essay · {page.gallery.length}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {page.gallery.map((u, idx) => (
              <a
                key={`${u}-${idx}`}
                href={resolveImg(u)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`gallery-photo-${idx}`}
                className="block bg-[#F2EDE5]"
              >
                <img src={resolveImg(u)} alt={`${page.title} — illustration ${idx + 1}`} className="w-full h-auto" />
              </a>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

// True for any video uploaded via /api/upload/video — these are served by
// /api/files/{id} and stream natively through <video controls>.
function isSelfHostedVideo(url) {
  if (!url) return false;
  // Match relative path or absolute (after host) so the resolveImg() prefix
  // works the same in dev and prod.
  return /\/api\/files\//.test(url);
}

// Convert public YouTube / Vimeo URLs into embeddable form so the iframe loads.
function toEmbedSrc(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    // YouTube watch?v=ID → /embed/ID
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) {
      return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    }
    // youtu.be/ID short links
    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    // Vimeo /ID → /video/ID
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean)[0];
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
  } catch (parseErr) {
    // URL constructor threw on a malformed input — fall through and return
    // the raw string so the iframe still tries to load it.
    logger.debug("toEmbedSrc parse fall-through", parseErr);
  }
  return url;
}
